export const en = {
  common: {
    back: 'Back', next: 'Next', cancel: 'Cancel', confirm: 'Confirm', close: 'Close',
    save: 'Save', done: 'Done', retry: 'Try again', loading: 'Loading',
    today: 'Today', tomorrow: 'Tomorrow', hours: 'hrs', perHour: '/hour',
    somethingWrong: 'Something went wrong.',
  },
  nav: { shifts: 'Shifts', myShifts: 'My shifts', learn: 'Learn', earnings: 'Earnings', profile: 'Profile' },
  auth: {
    title: 'Work when you want',
    subtitle: 'Vetted part-time shifts from employers across Malaysia. One profile, many workplaces.',
    phoneLabel: 'Mobile number',
    phoneHint: "We'll text you a 6-digit code",
    sendCode: 'Send code',
    codeTitle: 'Enter your code',
    codeSentTo: 'We sent a 6-digit code to {phone}',
    codeLabel: '6-digit code',
    verify: 'Verify',
    resend: 'Resend code',
    changeNumber: 'Change number',
    demoHint: 'Demo mode — the code is 123456',
    invalidPhone: 'Enter a valid Malaysian mobile number.',
  },
  vetting: {
    title: 'Getting you ready',
    subtitle: 'Employers hire from Pinch Crew because everyone here is checked. A few steps and you can start claiming shifts.',
    progress: 'Vetting progress',
    stageStatus: {
      not_started: 'Not started', submitted: 'Submitted', in_review: 'In review',
      passed: 'Done', failed: 'Action needed',
    },
    basicsTitle: 'What should we call you?',
    basicsBody: 'Your name as it appears on your MyKad. Employers see this when you claim a shift.',
    basicsName: 'Full name',
    basicsNamePlaceholder: 'e.g. Nur Aisyah binti Rahman',
    basicsEmail: 'Email (optional)',
    basicsEmailHelp: 'A second way to sign in and get receipts.',
    basicsPostcode: 'Your postcode',
    basicsPostcodeHelp: 'We use this to show you shifts near home. Five digits.',
    basicsPostcodeFound: 'Near {area}',
    basicsPostcodeUnknown: 'We do not recognise that postcode. Check the five digits.',
    basicsSave: 'Save and continue',
    bankTitle: 'Where should we pay you?',
    bankBody: 'We pay into this account after each approved shift. Ops checks it once before your first payout.',
    bankName: 'Bank',
    bankAccountNo: 'Account number',
    bankHolder: 'Name on the account',
    bankHolderHelp: 'Must match the bank exactly, or the transfer bounces.',
    bankSave: 'Save account',
    bankAdd: 'Add payout account',
    bankEdit: 'Change account',
    bankVerified: 'Verified',
    bankPending: 'We are checking this account',
    bankMissing: 'Add a payout account so we can pay you.',
    submitted: 'Submitted for review',
    submittedBody: "We have your documents. A reviewer checks them within 48 hours and we'll notify you the moment anything changes.",
    aboutYou: 'About you',
    aboutYouBody: 'A few questions so we can match you to the right shifts. Takes two minutes.',
    unlocks: 'Unlocks {what}',
    required: 'Required',
    optional: 'Optional',
    documents: 'Your documents',
    upload: 'Upload',
    replace: 'Replace',
    docStatus: { pending: 'In review', verified: 'Verified', rejected: 'Rejected' },
    docType: {
      ic: 'MyKad (IC)', bank: 'Bank account', student_pass: 'Student pass',
      work_permit: 'Work permit', food_handling_cert: 'Food handling certificate',
      typhoid_jab: 'Typhoid vaccination',
    },
    allDone: "You're verified",
    allDoneBody: 'You can claim any shift you qualify for.',
  },
  shifts: {
    title: 'Shifts for you',
    subtitle: 'Within {km} km of your home area',
    subtitleNoHome: 'Showing shifts everywhere · set your area to filter by distance',
    setHome: 'Set your area',
    empty: 'No open shifts right now',
    emptyNotActive: 'Finish your checks to start claiming',
    emptyNotActiveBody: '{n} shift is waiting. You can claim as soon as your vetting is approved. | {n} shifts are waiting. You can claim as soon as your vetting is approved.',
    emptyNoSkills: 'Add a skill to see matching shifts',
    emptyNoSkillsBody: 'Shifts are matched to what you can do. Complete a short course to unlock your first skill.',
    emptyTierTooLow: 'These shifts need a higher tier',
    emptyTierTooLowBody: '{n} shift is open in your line of work, but at a tier above yours. Finish a course to level up. | {n} shifts are open in your line of work, but at a tier above yours. Finish a course to level up.',
    emptyOtherSkills: 'No shifts in your skills right now',
    emptyOtherSkillsBody: '{n} shift is open in another skill. Learn it to widen what you can claim. | {n} shifts are open in other skills. Learn one to widen what you can claim.',
    emptyBody: "We'll notify you the moment a workplace near you needs crew.",
    emptyAction: 'Set my availability',
    funded: 'Funded',
    fundedExplain: 'The employer has already paid. Your wages are secured before you start.',
    startingSoon: 'Starting soon',
    spotsLeft: '{n} spot left | {n} spots left',
    claim: "I'll take it",
    confirmTitle: 'Take this shift?',
    confirmBody: 'You are committing to work at {site} on {when}. Not turning up affects your reliability score, so only take it if you can be there.',
    confirmCta: "Yes, I'll be there",
    confirmBack: 'Not yet',
    confirmPay: 'You will earn',
    claiming: 'Taking it…',
    claimed: 'Shift confirmed',
    claimedBody: "See you at {site}. We've added it to your shifts.",
    details: 'Shift details',
    dressCode: 'What to wear',
    instructions: 'Where to report',
    breakPaid: '{n} min paid break',
    estimatedPay: 'Estimated pay',
    payExplain: '{rate}/hour × {hours} hrs',
    tierRequired: 'Tier {n} required',
    filters: 'Filters',
    filterAll: 'All',
    filterTonight: 'Tonight',
    filterWeekend: 'Weekend',
    errors: {
      shift_full: 'Someone just took the last spot.',
      tier_too_low: 'You need a higher tier for this shift. Check Learn to unlock it.',
      time_conflict: 'This overlaps a shift you have already taken.',
    },
  },
  myShifts: {
    title: 'My shifts',
    upcoming: 'Upcoming',
    past: 'Past',
    emptyUpcoming: 'No upcoming shifts',
    emptyUpcomingBody: 'Browse open shifts and take one.',
    emptyPast: 'No past shifts yet',
    browse: 'Browse shifts',
    clockIn: 'Clock in',
    clockOut: 'Clock out',
    clockedInAt: 'Clocked in at {time}',
    onBreak: 'On break',
    startBreak: 'Start break',
    endBreak: 'End break',
    cancelShift: 'Cancel this shift',
    cancelConfirm: 'Cancel this shift?',
    cancelBody: 'Cancelling close to the start time affects your reliability score and may pause your access to shifts.',
    cancelKeep: 'Keep it',
    cancelYes: 'Yes, cancel',
    cancelled: 'Shift cancelled',
    completed: 'Completed',
    noShow: 'No-show',
    worked: 'Worked {hours} hrs',
  },
  clock: {
    title: 'Clock in',
    titleOut: 'Clock out',
    scanPrompt: 'Scan the QR code at {site}',
    scanBody: 'Ask the duty manager where the Pinch Crew code is displayed.',
    enterCode: 'Clock in',
    manualLabel: 'Check-in code',
    manualHint: 'Type the code shown on the Pinch Crew screen at your site.',
    cameraFailed: 'We could not open the camera. Type the code instead.',
    scan: 'Scan QR code',
    success: "You're clocked in",
    successBody: 'Have a good shift.',
    successOut: "You're clocked out",
    successOutBody: 'Your hours have been sent for approval. Payment follows the next payout run.',
    late: 'You clocked in {n} min late',
    errors: {
      invalid_qr: 'That QR code is not a Pinch Crew check-in code.',
      wrong_site: 'This code is for another workplace.',
      already_clocked_in: 'You are already clocked in.',
      not_clocked_in: 'You have not clocked in yet.',
    },
  },
  earnings: {
    title: 'Earnings',
    pendingApproval: 'Awaiting approval',
    pendingBody: 'Employers have {n} hrs to check your hours. After that it approves automatically.',
    nextPayout: 'Next payout',
    paidOut: 'Paid out',
    thisMonth: 'This month',
    allTime: 'All time',
    timesheets: 'Recent shifts',
    payouts: 'Payout history',
    tsStatus: { pending: 'Awaiting approval', approved: 'Approved', disputed: 'Disputed', settled: 'Paid' },
    poStatus: { pending: 'Scheduled', processing: 'Processing', paid: 'Paid', failed: 'Failed' },
    autoApproves: 'Auto-approves {when}',
    dispute: 'These hours look wrong',
    disputeTitle: 'Report a problem',
    disputeBody: 'Tell us what the correct hours were. We will check with the employer.',
    disputeReason: 'What happened?',
    disputeHours: 'Hours you actually worked',
    disputeSubmit: 'Send report',
    disputeSent: 'Report sent',
    empty: 'No earnings yet',
    emptyBody: 'Complete your first shift and it will show up here.',
  },
  learn: {
    title: 'Learn',
    subtitle: 'Finish a course, raise your tier, earn more per hour.',
    unlockRate: 'Unlock +RM{amount}/hour',
    startCourse: 'Start course',
    continueCourse: 'Continue',
    reviewCourse: 'Review',
    completed: 'Completed',
    duration: '{n} min',
    modules: 'Modules',
    moduleDone: 'Done',
    markDone: 'Mark as done',
    quiz: 'Knowledge check',
    quizIntro: 'Answer at least 80% correctly to earn your certificate.',
    startQuiz: 'Start the check',
    submitQuiz: 'Submit answers',
    quizPassed: 'You passed',
    quizPassedBody: 'Score {score}%. Your {skill} tier is now T{tier} — you can claim better-paid shifts.',
    quizFailed: 'Not quite',
    quizFailedBody: 'Score {score}%. Review the modules and try again — there is no limit on attempts.',
    tryAgain: 'Try again',
    certificates: 'Your certificates',
    certIssued: 'Issued {date}',
    currentTier: 'Your tier: T{n}',
  },
  referrals: {
    title: 'Invite a friend',
    subtitle: 'Good crew know good crew. You both get paid when they finish their first shift.',
    yourCode: 'Your code',
    share: 'Share',
    copied: 'Copied',
    youGet: 'You get RM{amount}',
    theyGet: 'They get RM{amount}',
    invited: '{n} invited',
    waiting: '{n} waiting for a first shift',
    earned: 'RM{amount} earned',
    haveCode: 'Got a code from a friend?',
    enterCode: 'Enter their code',
    applyCode: 'Apply',
    codeApplied: 'Code applied',
  },
  profile: {
    title: 'Profile',
    reliability: 'Reliability',
    reliabilityBody: 'Based on shifts completed, punctuality and cancellations.',
    skills: 'Your skills',
    tierLabel: 'Tier {n}',
    ratings: 'What employers say',
    noRatings: 'No ratings yet',
    homeArea: 'Where do you live?',
    homeAreaHelp: 'Your postcode. We use it to show shifts near home.',
    travelNeedsHome: 'Add your postcode above and we can filter shifts by distance.',
    travelRadius: 'How far will you travel?',
    travelValue: '{n} km',
    availability: 'When can you work?',
    documents: 'Documents',
    language: 'Language',
    signOut: 'Sign out',
    memberSince: 'On Pinch Crew since {date}',
  },
}
